import { Component, type ErrorInfo, type ReactNode } from 'react'
import i18n from '@/i18n'
import { errorReporter } from '@/lib/errorReporter'

interface AppErrorBoundaryProps {
  children: ReactNode
}

interface AppErrorBoundaryState {
  hasError: boolean
}

/**
 * 최후의 보호선. 렌더/커밋 중 잡히지 않은 에러로 React 가 트리 전체를
 * 언마운트해 빈 화면이 되는 대신, 복구 안내와 새로고침 버튼을 보여준다.
 * (클래스 컴포넌트라 useTranslation 을 쓸 수 없어 i18n.t 를 직접 사용)
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Unhandled render error:', error, errorInfo.componentStack)
    errorReporter.reportError(error, {
      errorType: 'boundary',
      boundary: 'AppErrorBoundary',
      componentStack: errorInfo.componentStack ?? undefined,
    })
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="text-base font-semibold text-slate-900">{i18n.t('common.crashTitle')}</p>
          <p className="mt-2 text-sm text-slate-500">{i18n.t('common.crashDescription')}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            {i18n.t('common.crashReload')}
          </button>
        </div>
      </div>
    )
  }
}
